import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { combineLatest, Observable, of, race, timer } from 'rxjs';
import { catchError, filter, map, switchMapTo, take } from 'rxjs/operators';
import { environment } from '~environments/environment';
import { Frac, FracClusterTruckingVendor, FracVendorAllocationStats } from '~lmo/models/frac.model';
import { LmoFracFctvsService } from '~lmo/services/lmo-frac-fctvs.service';
import { trackById } from '~utilities/trackById';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'sa-lmo-frac-home-trucking-vendors',
  styleUrls: ['./lmo-frac-home-trucking-vendors.component.scss'],
  templateUrl: './lmo-frac-home-trucking-vendors.component.html',
})
export class LmoFracHomeTruckingVendorsComponent implements OnInit {
  @Input() public frac: Frac;
  public fracClusterTruckingVendors$: Observable<FracClusterTruckingVendor[]>;
  public overflowCount$: Observable<number>;
  public trackById = trackById;

  constructor(private fracFctvsService: LmoFracFctvsService, private http: HttpClient) {}

  public ngOnInit() {
    const filteredFctvs$ = race(
      this.fracFctvsService.currentFracFctvs$.pipe(filter((fctvs) => !!fctvs.length)),
      timer(3000, 0).pipe(take(1), switchMapTo(this.fracFctvsService.currentFracFctvs$)),
    ).pipe(
      map((fctvs) => fctvs.filter((fctv) => this.isAwarded(fctv) || this.isPending(fctv))),
      map((fctvs) => fctvs.filter((fctv) => fctv.allocationPercent !== 0)),
    );

    const stats$ = this.http
      .get<FracVendorAllocationStats>(`${environment.api}/frac/${this.frac.id}/vendor_allocation_stats`)
      .pipe(catchError(() => of(null as FracVendorAllocationStats | null)));

    this.fracClusterTruckingVendors$ = combineLatest([filteredFctvs$, stats$]).pipe(
      map(([fctvs, stats]) => {
        if (!stats || !stats.vendors) {
          return fctvs;
        }
        const byId = new Map(stats.vendors.map((vendor) => [vendor.id, vendor]));
        return fctvs.map((fctv) => {
          const stat = byId.get(fctv.id);
          if (!stat) {
            return fctv;
          }
          return {
            ...fctv,
            currentTruckPoolAllocationPercent: stat.currentTruckPoolAllocationPercent,
          };
        });
      }),
    );

    this.overflowCount$ = this.fracFctvsService.currentFracOverflowVendors$.pipe(
      map((vendors) =>
        (vendors || []).filter((vendor) => {
          return vendor.contracts && vendor.contracts.length && vendor.contracts[0].isCurrentClusterOverflowVendor;
        }),
      ),
      map((vendors) => (vendors || []).length),
    );
  }

  public isAwarded(fctv: FracClusterTruckingVendor) {
    return fctv.status === 'awarded';
  }

  public isPending(fctv: FracClusterTruckingVendor) {
    return fctv.status === 'pending';
  }
}
